import { type KeyboardShortcutCommand, Node, mergeAttributes } from '@tiptap/core'
import { Selection } from '@tiptap/pm/state'

import { ASSET_URLS } from '../../../constants'

export const EmailHeader = Node.create({
  name: 'emailHeader',
  group: 'block',
  atom: true,
  draggable: false,
  selectable: false,

  addAttributes() {
    return {
      logoUrl: {
        default: '',
        parseHTML: (element: HTMLElement): string => element.getAttribute('data-logo-url') || '',
        renderHTML: (attributes: Record<string, string>): Record<string, string> => ({
          'data-logo-url': attributes.logoUrl,
        }),
      },
      organizationName: {
        default: '',
        parseHTML: (element: HTMLElement): string =>
          element.getAttribute('data-organization-name') || '',
        renderHTML: (attributes: Record<string, string>): Record<string, string> => ({
          'data-organization-name': attributes.organizationName,
        }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-type="email-header"]' }]
  },

  addKeyboardShortcuts(): Record<string, KeyboardShortcutCommand> {
    return {
      // Prevent Backspace from merging the textblock after the header into the atom
      // header node: swallow it at the textblock's start, or delete an empty textblock
      // outright (unless it's the doc's last node).
      Backspace: ({ editor }) =>
        editor.commands.command(({ tr, state, dispatch }) => {
          const { $from, empty } = state.selection
          if (!empty || !$from.parent.isTextblock) return false
          if ($from.depth !== 1 || $from.index(0) === 0) return false
          if (state.doc.child($from.index(0) - 1).type.name !== this.name) return false
          if ($from.parent.content.size > 0) return $from.parentOffset === 0
          if ($from.index(0) >= state.doc.childCount - 1) return false
          if (dispatch) {
            const from = $from.before()
            tr.delete(from, $from.after())
            tr.setSelection(Selection.near(tr.doc.resolve(from), 1))
          }
          return true
        }),
    }
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'email-header',
        class: 'email-header',
        contenteditable: 'false',
      }),
      [
        'img',
        {
          src: node.attrs.logoUrl || ASSET_URLS.logo,
          alt: node.attrs.organizationName,
          class: 'email-header-logo',
        },
      ],
      ['h1', { class: 'email-header-name' }, node.attrs.organizationName],
      ['hr', { class: 'email-divider' }],
    ]
  },
})

export const EmailFooter = Node.create({
  name: 'emailFooter',
  group: 'block',
  atom: true,
  draggable: false,
  selectable: false,

  parseHTML() {
    return [{ tag: 'div[data-type="email-footer"]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'email-footer',
        class: 'email-footer',
        contenteditable: 'false',
      }),
      ['hr', { class: 'email-divider' }],
      [
        'div',
        { class: 'email-footer-content' },
        [
          'img',
          {
            src: ASSET_URLS.icon,
            alt: 'Polimorphic',
            class: 'email-footer-icon',
          },
        ],
        ['span', { class: 'email-footer-text' }, 'Powered by Polimorphic'],
      ],
    ]
  },
})

export const EmailAIReplyBody = Node.create({
  name: 'emailAIReplyBody',
  group: 'block',
  atom: true,
  draggable: false,
  selectable: true,

  parseHTML() {
    return [{ tag: 'div[data-type="ai-reply-body"]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'ai-reply-body',
        class: 'email-ai-reply-body',
        contenteditable: 'false',
      }),
      [
        'span',
        { class: 'email-ai-reply-body-label' },
        'AI reply goes here (only used when this letter is attached to an AI email)',
      ],
    ]
  },
})
